import { useMsal } from '@azure/msal-react';
import StyledDateRangePicker from '@components/date-range-select/StyledDateRangePicker';
import { DrawerContext, DrawerPage } from '@components/drawer/useDrawer';
import FilterRoot from '@components/filter/FilterRoot';
import PopUpWarning from '@components/PopUpWarning';
import useStyledAutocomplete from '@components/styled-autocomplete/useStyledAutocomplete';
import { pushError, pushSuccess } from '@components/toast';
import { AxiosError } from '@controllers/axiosClient';
import { cameraTaskAttachmentHttpController, locationController, taskHttpController } from '@controllers/index';
import { DEFAULT_PAGE_SIZE, defaultPagination, ListProps, Paging } from '@Core';
import { IGetCameraTaskProps } from '@EcamModel/controllers/ICameraTaskHttpController';
import { Location } from '@EcamModel/model';
import { CameraTask, TaskPriority, TaskStatus } from '@EcamModel/model/CameraTask';
import { CameraTaskAttachment } from '@EcamModel/model/CameraTaskAttachment';
import useMedia from '@hooks/useMedia';
import usePopUp from '@hooks/usePopUp';
import { Button, Pagination as MuiPagination, Stack, SxProps, Theme, Tooltip, Typography } from '@mui/material';
import { useCameraDetailContext } from '@pages/cameras/hook/CameraDetailsProvider';
import { Filter } from '@pages/cameras/list';
import { CameraOverviewPopup } from '@pages/camerasOverview/configs';
import color from '@theme/Colors';
import moment from 'moment';
import { useContext, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDebounce } from 'usehooks-ts';
import { IGetLocationProps } from '../../submodules/ecam-ops-base-model/eCam-model/controllers/ILocationHttpController';
import FilterChildForTaskManagement from './components/FilterChildForTaskManagement';
import ListDataTask from './components/ListDataTask';
import PopupCompleteTask from './components/popup/PopupCompleteTask';
import PopUpCountAttachments from './components/popup/PopUpCountAttachments';
import PopupUpsertTask from './components/popup/PopupUpsertTask';
import PopupViewCompleteTask from './components/popup/PopupViewCompleteTask';
import {
    AttachmentsFiles,
    DropdownType,
    HandleConfirmParams,
    mappingPriorityTask,
    mappingStatusTask,
    OwnFilterCameraTask,
    SearchType,
    searchTypeMapping,
    searchTypes,
    TypeHandleConfirm,
} from './config';
import CreateManualPCN from '@assets/jsx-icon/CreateManualPCN';

export type LoadingProps = {
    popupConfirm: boolean;
    getData: boolean;
};

type Props = {
    isShowTaskManagement?: boolean;
};
export default function TaskManagementScreen(props: Props) {
    const { setActiveItem } = useContext(DrawerContext);
    const { accounts } = useMsal();
    const geoZonesContext = useCameraDetailContext();
    const { refetchTrailAuditEvent } = geoZonesContext;
    const { isMobileSM } = useMedia();

    const queryClient = useQueryClient();
    const { id: cameraId } = useParams();
    const { state, search, pathname } = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(search);
    const taskName = decodeURIComponent(queryParams.get('taskName') || '');
    const taskId = decodeURIComponent(queryParams.get('taskId') || '');

    const isActionCancelCompletePopup = useRef<boolean>(false);

    const [taskIdParams, setTaskIdParams] = useState<string | undefined>(taskId || '');
    console.log('🚀 ~ TaskManagementScreen ~ taskId, taskName:', { taskId, taskName });

    const initFilter = {
        page: 1,
        searchType: searchTypes[state?.cameraName ? 1 : 0],
        search: state?.cameraName || taskName || '',
        fromStart: state?.created ? moment(state.created).toDate() : undefined,
        toStart: state?.created ? moment(state.created).toDate() : undefined,
    };

    const [filter, setFilter] = useState<Partial<Filter>>({
        ...initFilter,
        page: 1,
    });

    const [loadingState, setLoadingState] = useState<LoadingProps>({
        getData: true,
        popupConfirm: false,
    });

    const [selectedTaskAttribute, setSelectedTaskAttribute] = useState<TaskStatus | TaskPriority | null>(null);
    const [taskItem, setTaskItem] = useState<CameraTask>({} as CameraTask);
    console.log('🚀 ~ TaskManagementScreen ~ taskItem:', taskItem);
    const [listLocation, setListLocation] = useState<Paging<Location>>(defaultPagination());
    const [filterSortState, setFilterSortState] = useState<OwnFilterCameraTask>({
        sorts: [],
    } as OwnFilterCameraTask);

    const [cameraOverview, setCameraOverview] = useState<CameraOverviewPopup>({
        cameraId: Number(cameraId),
        cameraName: '',
        locationName: '',
    });

    const [uploadedAttachmentsFile, setUploadedAttachmentsFile] = useState<AttachmentsFiles[]>([]);
    const [attachmentsFileItem, setAttachmentsFileItem] = useState<AttachmentsFiles>({} as AttachmentsFiles);

    const popupUpsertTask = usePopUp();
    const popupCompleteTask = usePopUp();
    const popUpCountAttachments = usePopUp();
    const popupViewCompleteTask = usePopUp();
    const popupConfirmChangeStatus = usePopUp();
    const popupConfirmChangePriority = usePopUp();
    const popupConfirmDeleteTask = usePopUp();

    const _searchType = searchTypes.find((_search) => _search.Id === Number(filter.searchType?.Id)) || searchTypes[0];

    const locationAutocomplete = useStyledAutocomplete({
        async getList(props: IGetLocationProps): Promise<Paging<Location>> {
            const res = await locationController.listHasCamera(props).finally(() => {});
            setListLocation(res);
            return res;
        },
        listProps: {
            search: { fields: ['name'] } as any,
        },
    });

    const setPartialFilter = (filter: Partial<Filter>) => {
        setFilter((prev) => ({ ...prev, ...filter, page: 1 }));
        setTaskIdParams(undefined);
    };
    const debouncedValue = useDebounce<string>(filter.search ?? '', 500);

    const setPartialLoading = (loadings: Partial<LoadingProps>) => {
        setLoadingState((prev) => ({ ...prev, ...loadings }));
    };

    const handleResetFilter = (isResetFilter: boolean) => {
        setFilter({
            page: 1,
            searchType: isResetFilter ? searchTypes[0] : searchTypes[2],
            search: isResetFilter ? `` : `${accounts?.[0].username ?? ''}`,
            fromStart: undefined,
            toStart: undefined,
            fromEnd: undefined,
            toEnd: undefined,
        });
        setTaskIdParams(undefined);

        setFilterSortState((prev) => ({ ...prev, sorts: isResetFilter ? [] : ['TaskStatus', 'EndDate'] }));

        const newUrl = pathname;
        navigate(newUrl, { replace: true });
    };

    const handleChangeStatusOrPriority = (
        selectedItem: TaskStatus | TaskPriority | null,
        taskId: number,
        type: DropdownType
    ) => {
        const taskToUpdate = listTask?.rows?.find((task) => task.Id === taskId);

        if (!taskToUpdate) return;

        setTaskItem(taskToUpdate);

        if (selectedItem === null) return;

        if (type === DropdownType.Status) {
            if (selectedItem === TaskStatus.Completed) {
                isActionCancelCompletePopup.current = true;
                popupCompleteTask.setTrue();
                return;
            }
            if (Object.values(TaskStatus).includes(selectedItem as TaskStatus)) {
                setSelectedTaskAttribute(selectedItem as TaskStatus);
                popupConfirmChangeStatus.setTrue();
            }
        } else if (type === DropdownType.Priority) {
            if (Object.values(TaskPriority).includes(selectedItem as TaskPriority)) {
                setSelectedTaskAttribute(selectedItem as TaskPriority);
                popupConfirmChangePriority.setTrue();
            }
        }
    };

    const getListTask = async () => {
        const _filter: IGetCameraTaskProps = {
            page: filter.page ?? 1,
            pageSize: DEFAULT_PAGE_SIZE,
            LocationId: filter.location?.Id,
            SearchByCameraName: _searchType.Value === SearchType.Camera ? filter.search : undefined,
            sorts: !filterSortState.sorts?.length ? ['-Created'] : filterSortState.sorts,
            StartDate: {
                From: filter.fromStart!,
                To: filter.toStart!,
            },
            EndDate: {
                From: filter.fromEnd!,
                To: filter.toEnd!,
            },
            ZoneIds:
                filter.zones?.reduce<number[]>((acc, z) => {
                    if (z.Id !== undefined) acc.push(z.Id);
                    return acc;
                }, []) ?? undefined,
            search: {
                fields: searchTypeMapping[_searchType?.Value] ?? [],
                content: filter.search ?? '',
            },
            filter: {
                Id: Number(taskIdParams) || undefined,
                CameraId: props.isShowTaskManagement ? Number(cameraId) : undefined,
                TaskStatus: filter.status?.Value,
                TaskPriority: filter.taskPriorities?.Value,
            },
        };

        if (_searchType.Value === SearchType.Camera) delete _filter.search;

        const response = await taskHttpController.list(_filter);
        return response;
    };

    const {
        data: listTask,
        error: errorListTask,
        isLoading: isLoadingListTask,
    } = useQuery(
        [
            'listTask',
            cameraId,
            debouncedValue,
            filter.page,
            filter.location,
            filter.zones,
            filter.taskPriorities,
            filter.fromStart,
            filter.fromEnd,
            filter.toStart,
            filter.toEnd,
            filter.status,
            filterSortState.sorts,
            filter.search ? filter.searchType : undefined,
        ],
        getListTask,
        {
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            enabled: Boolean(filter.searchType),
        }
    );

    const upsertCameraTaskAPI = async (_task: CameraTask) => {
        const response = await taskHttpController.upsert(_task);
        return response;
    };

    const deleteCameraTaskAPI = async (taskId: string) => {
        const response = await taskHttpController.delete(taskId);
        return response;
    };

    const mutationHandleTask = useMutation(
        (task: CameraTask | string) =>
            typeof task === 'string' ? deleteCameraTaskAPI(task) : upsertCameraTaskAPI(task),
        {
            onSuccess: async (res) => {
                queryClient.invalidateQueries(['listTask']);
                if (props.isShowTaskManagement) {
                    await refetchTrailAuditEvent();
                }

                queryClient.setQueryData<CameraTask[]>(['listTask'], (oldData) => {
                    if (!oldData) return oldData!;
                    return oldData.map((task) => (task.Id === res.Id ? { ...task, ...res } : task));
                });
            },
            onError: (err: AxiosError) => {
                pushError(err.message);
            },
            onSettled: () => {
                setPartialLoading({ popupConfirm: false });
            },
        }
    );

    const handleConfirm = async ({
        type,
        task,
        selectedAttribute,
        onSuccessMessage,
        onClosePopup,
    }: HandleConfirmParams) => {
        try {
            setPartialLoading({ popupConfirm: true });

            let updatedTask: CameraTask = {
                Id: task.Id,
            } as CameraTask;

            if (type === TypeHandleConfirm.Status) {
                updatedTask = {
                    ...updatedTask,
                    TaskStatus: selectedAttribute as TaskStatus,
                    AttendingDate: moment().toDate(),
                };
            } else if (type === TypeHandleConfirm.Priority) {
                updatedTask = {
                    ...updatedTask,
                    TaskPriority: selectedAttribute as TaskPriority,
                };
            }

            if (type === TypeHandleConfirm.Delete) {
                await mutationHandleTask.mutateAsync(task.Id?.toString()!);
            } else {
                await mutationHandleTask.mutateAsync(updatedTask);
            }

            pushSuccess(onSuccessMessage);

            onClosePopup();
        } catch (error) {
            pushError((error as AxiosError).message);
        } finally {
            setPartialLoading({ popupConfirm: false });
        }
    };

    useEffect(() => {
        setActiveItem(DrawerPage.TaskManagement);
    }, []);

    // Get list of attachments
    const handleGetTaskAttachments = async () => {
        const _filter: ListProps<CameraTaskAttachment> = {
            pageSize: 12,
            page: 1,
            filter: {
                CameraTaskId: taskItem.Id,
            },
        };
        if (taskItem.Id) {
            const response = await cameraTaskAttachmentHttpController.list(_filter);

            queryClient.setQueryData<CameraTask[]>(['listTask'], (oldData) => {
                if (!oldData) return oldData!;
                return oldData.map((task) =>
                    task.Id === taskItem.Id ? { ...task, CameraTaskAttachments: response.rows } : task
                );
            });

            return response;
        }
    };

    const {
        data: listTaskAttachments,
        error: errorListTaskAttachments,
        isLoading: isLoadingListTaskAttachments,
        isFetching: isFetchingListTaskAttachments,
        refetch: refetchListTaskAttachments,
    } = useQuery(['listTaskAttachments', taskItem.Id], handleGetTaskAttachments, {
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        setActiveItem(props.isShowTaskManagement ? DrawerPage.Cameras : DrawerPage.TaskManagement);
    }, []);

    useEffect(() => {
        if (!state) return;
        setFilter({
            ...initFilter,
            fromEnd: undefined,
            toEnd: undefined,
        });
        setTaskIdParams(undefined);
    }, [state]);

    return (
        <Stack
            direction={'column'}
            spacing={2}
            sx={
                props.isShowTaskManagement
                    ? {
                          padding: 2,
                          borderRadius: '0px 16px 16px 16px',
                          border: '1px solid #E4E7EC',
                      }
                    : {}
            }
        >
            {/* title */}
            {!props.isShowTaskManagement && (
                <Stack direction="row" justifyContent={'space-between'} alignItems={'center'}>
                    <Typography variant="h3">Task management</Typography>
                    <Stack direction={'row'} spacing={1}>
                        <Button
                            variant="contained"
                            onClick={() => {
                                popupUpsertTask.setTrue();
                            }}
                        >
                            Create task
                        </Button>
                        <Tooltip title="Create PCN" placement="bottom">
                            <Button
                                variant="outlinedGreen"
                                sx={{ p: 1, minWidth: 0 }}
                                onClick={() => {
                                    navigate('/manual-creation-pcn');
                                }}
                            >
                                <CreateManualPCN />
                            </Button>
                        </Tooltip>
                    </Stack>
                </Stack>
            )}

            {/* filter */}
            <FilterRoot
                isCollapse={isMobileSM ? true : false}
                buttonFilterDiff={
                    <Button
                        variant="contained"
                        sx={{
                            padding: '0.25rem 1rem',
                            fontSize: '0.75rem',
                            lineHeight: '1rem',
                        }}
                        onClick={() => {
                            handleResetFilter(false);
                        }}
                    >
                        My task filter
                    </Button>
                }
                rightTitleContent={
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        alignItems={'center'}
                        sx={{ width: { xs: '100%', lg: 720 } }}
                    >
                        <StyledDateRangePicker
                            value={{
                                startDate: filter.fromStart,
                                endDate: filter.toStart,
                            }}
                            onChange={(value) => {
                                setPartialFilter({ fromStart: value.startDate, toStart: value.endDate });
                            }}
                            dateReset={{ startDate: undefined, endDate: undefined }}
                            placeholder="Select created date"
                            label={'Created date'}
                            disableLoading={isLoadingListTask}
                        />

                        <StyledDateRangePicker
                            value={{
                                startDate: filter.fromEnd,
                                endDate: filter.toEnd,
                            }}
                            onChange={(value) => {
                                setPartialFilter({ fromEnd: value.startDate, toEnd: value.endDate });
                            }}
                            dateReset={{ startDate: undefined, endDate: undefined }}
                            placeholder="Select due date"
                            label={'Due date'}
                            disableLoading={isLoadingListTask}
                        />
                    </Stack>
                }
                filterChild={
                    <FilterChildForTaskManagement
                        filter={filter}
                        loadingState={isLoadingListTask}
                        locationAutocomplete={locationAutocomplete}
                        onSetPartialFilter={setPartialFilter}
                        isShowTaskManagement={props.isShowTaskManagement}
                    />
                }
                handleResetFilter={() => {
                    handleResetFilter(true);
                }}
            />

            {/* list */}
            <ListDataTask
                isShowTaskManagement={props.isShowTaskManagement}
                listTask={listTask!}
                isLoading={isLoadingListTask}
                filter={filter}
                taskItem={taskItem}
                popupConfirmDeleteTask={popupConfirmDeleteTask}
                popupViewCompleteTask={popupViewCompleteTask}
                popUpCountAttachments={popUpCountAttachments}
                popupUpsertTask={popupUpsertTask}
                filterSortState={filterSortState}
                onSetFilterSortState={setFilterSortState}
                onSetPartialFilter={setPartialFilter}
                onSetTaskItem={setTaskItem}
                onSetFilter={setFilter}
                handleChangeStatusOrPriority={handleChangeStatusOrPriority}
            />

            {/* paging */}
            {listTask?.total! > listTask?.pageSize! && !isLoadingListTask && (
                <Stack mt={3} direction="row" justifyContent="center">
                    <MuiPagination
                        count={listTask?.totalPages || 1}
                        page={listTask?.page}
                        onChange={(_, page) => setFilter((prev) => ({ ...prev, page: page }))}
                        variant="outlined"
                        shape="rounded"
                        siblingCount={isMobileSM ? 0 : 1}
                    />
                </Stack>
            )}

            {/* Popup */}
            <PopupUpsertTask
                {...popupUpsertTask}
                isShowTaskManagement={props.isShowTaskManagement}
                cameraOverview={cameraOverview}
                popupCompleteTask={popupCompleteTask}
                locationAutocomplete={locationAutocomplete}
                listTaskAttachments={listTaskAttachments}
                isFetchingListTaskAttachments={isFetchingListTaskAttachments}
                isLoadingListTaskAttachments={isLoadingListTaskAttachments}
                taskItem={taskItem}
                listLocation={listLocation}
                uploadedAttachmentsFile={uploadedAttachmentsFile}
                attachmentsFileItem={attachmentsFileItem}
                onSetAttachmentsFileItem={setAttachmentsFileItem}
                setUploadedAttachmentsFile={setUploadedAttachmentsFile}
                onSetCameraOverview={setCameraOverview}
                onSetTaskItem={setTaskItem}
            />

            <PopupCompleteTask
                {...popupCompleteTask}
                isActionCancelCompletePopup={isActionCancelCompletePopup}
                isShowTaskManagement={props.isShowTaskManagement}
                popupUpsertTask={popupUpsertTask}
                taskItem={taskItem}
                onSetTaskItem={setTaskItem}
            />
            <PopupViewCompleteTask {...popupViewCompleteTask} taskItem={taskItem} onSetTaskItem={setTaskItem} />

            <PopUpWarning
                {...popupConfirmChangeStatus}
                isLoading={loadingState.popupConfirm}
                title="Confirm"
                message={`Are you sure you want to change the status from ${mappingStatusTask?.[
                    taskItem.TaskStatus
                ]?.toUpperCase()} to ${mappingStatusTask[selectedTaskAttribute as TaskStatus]?.toUpperCase()}?`}
                onClose={() => {
                    setTaskItem({} as CameraTask);
                    popupConfirmChangeStatus.onClose();
                }}
                onConfirm={() => {
                    handleConfirm({
                        type: TypeHandleConfirm.Status,
                        task: taskItem,
                        selectedAttribute: selectedTaskAttribute as TaskStatus,
                        onSuccessMessage: 'Change status successfully',
                        onClosePopup: () => {
                            setTaskItem({} as CameraTask);
                            popupConfirmChangeStatus.onClose();
                        },
                    });
                }}
            />
            <PopUpWarning
                {...popupConfirmChangePriority}
                isLoading={loadingState.popupConfirm}
                title="Confirm"
                message={`Are you sure you want to change the priority from ${mappingPriorityTask?.[
                    taskItem.TaskPriority ?? ''
                ]?.toUpperCase()} to ${mappingPriorityTask?.[selectedTaskAttribute as TaskPriority]?.toUpperCase()}?`}
                onClose={() => {
                    setTaskItem({} as CameraTask);
                    popupConfirmChangePriority.onClose();
                }}
                onConfirm={() => {
                    handleConfirm({
                        type: TypeHandleConfirm.Priority,
                        task: taskItem,
                        selectedAttribute: selectedTaskAttribute as TaskPriority,
                        onSuccessMessage: 'Change priority successfully',
                        onClosePopup: () => {
                            setTaskItem({} as CameraTask);
                            popupConfirmChangePriority.onClose();
                        },
                    });
                }}
            />

            <PopUpWarning
                {...popupConfirmDeleteTask}
                isLoading={loadingState.popupConfirm}
                title="Confirm"
                message={`Are you sure you want to delete this task?`}
                onClose={() => {
                    setTaskItem({} as CameraTask);
                    popupConfirmDeleteTask.onClose();
                }}
                onConfirm={() => {
                    handleConfirm({
                        type: TypeHandleConfirm.Delete,
                        task: taskItem,
                        onSuccessMessage: 'Delete successfully',
                        onClosePopup: () => {
                            setTaskItem({} as CameraTask);
                            popupConfirmDeleteTask.onClose();
                        },
                    });
                }}
            />

            <PopUpCountAttachments
                {...popUpCountAttachments}
                taskItem={taskItem}
                listTaskAttachments={listTaskAttachments}
                isFetchingListTaskAttachments={isFetchingListTaskAttachments}
                isLoadingListTaskAttachments={isLoadingListTaskAttachments}
            />
        </Stack>
    );
}

export const scrollbarStyles = {
    '&::-webkit-scrollbar': {
        height: '5px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
        cursor: 'pointer',
        backgroundColor: color.grey600,
        borderRadius: '3px',
    },
    '::-webkit-scrollbar-track': {
        background: 'transparent',
    },
    '&::-webkit-scrollbar:vertical': {
        display: 'none',
    },
};

export const ellipsisStyles: SxProps<Theme> = {
    display: '-webkit-box',
    WebkitLineClamp: 3,
    overflow: 'hidden',
    WebkitBoxOrient: 'vertical',
    textOverflow: 'ellipsis',
    maxHeight: '5.5em',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
};
